import React, { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useMutualUnread } from "../../context/MutualUnreadContext";
import { RequiredActionsContext } from "../../context/RequiredActionsContext";
import AppModalShell, { AppModalButton } from "../common/AppModalShell";

export default function MutualConnectionsPromptModal() {
  const { t } = useTranslation("vault");
  const navigate = useNavigate();
  const mutualUnreadCtx = useMutualUnread();
  const requiredActionsCtx = useContext(RequiredActionsContext);
  const isRequiredActionsOpen = Boolean(requiredActionsCtx?.open);
  const unreadCount = Number(mutualUnreadCtx?.unreadCount || 0);
  const unreadUserIds = Array.isArray(mutualUnreadCtx?.unreadUserIds)
    ? mutualUnreadCtx.unreadUserIds
    : [];
  const [open, setOpen] = useState(false);
  const [dismissedCount, setDismissedCount] = useState(0);
  const [markingSeen, setMarkingSeen] = useState(false);

  useEffect(() => {
    // Keep strict order: Required Actions has priority over mutual prompt.
    if (isRequiredActionsOpen) {
      setOpen(false);
      return;
    }

    if (unreadCount <= 0) {
      setOpen(false);
      setDismissedCount(0);
      return;
    }

    if (unreadCount !== dismissedCount) {
      setOpen(true);
    }
  }, [dismissedCount, isRequiredActionsOpen, unreadCount]);

  const handleClose = () => {
    setDismissedCount(unreadCount);
    setOpen(false);
  };

  const handleOpenMutual = () => {
    setDismissedCount(unreadCount);
    setOpen(false);
    navigate("/vault/main?open_connections_map=1&open_mutual=1");
  };

  const handleSkip = async () => {
    if (markingSeen) return;
    setMarkingSeen(true);
    try {
      const displayedBatch = unreadUserIds.length === unreadCount
        ? unreadUserIds
        : undefined;
      await mutualUnreadCtx.markSeen(displayedBatch);
      setDismissedCount(unreadCount);
      setOpen(false);
    } catch (error) {
      console.error("Failed to mark mutual connections as seen", error);
    } finally {
      setMarkingSeen(false);
    }
  };

  if (!open || unreadCount <= 0) return null;

  return (
    <AppModalShell
      open={open}
      title={t("connections_map.new_mutual_modal_title")}
      onClose={handleClose}
      closeAriaLabel={t("connections_map.back_alt")}
      zIndexClassName="z-[13020]"
      maxWidthClassName="max-w-[34rem]"
      titleClassName="text-[22px] sm:text-[28px]"
      footer={
        <div className="grid grid-cols-1 gap-3">
          <AppModalButton onClick={handleOpenMutual} className="text-[#2563EB]">
            {t("connections_map.new_mutual_modal_cta")}
          </AppModalButton>
          <AppModalButton variant="secondary" onClick={handleSkip} loading={markingSeen}>
            {t("connections_map.new_mutual_modal_skip")}
          </AppModalButton>
        </div>
      }
    >
      <p className="text-sm leading-relaxed text-white/85">
        {t("connections_map.new_mutual_modal_body", { count: unreadCount })}
      </p>
    </AppModalShell>
  );
}
